/**
 * FedRAMP 20x Class A state — V103-6-3. ONE model for the Class A page and the
 * FedRAMP card / strip on Today: the Class A items (materials + clocks), the
 * Class A rules from the rules dataset (FedRAMP requirements required for
 * Class A), and the GovRAMP overview it rests on (the Ready booking is that
 * one record — acceptance #4).
 *
 * Reads only (any member).
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ProgramCard } from './api';
import { queryKeys } from './query-keys';
import { getClassA, getClassReadiness, getGovrampOverview, type ClassAClock, type ClassAMaterialsItem, type GovrampOverview } from './govramp-api';
import { classAHeroCounts, classARules, classAWorld, type ClassAHeroCounts, type ClassARule, type ClassAWorld } from './govramp-model';

export interface ClassAState {
  loading: boolean;
  error: boolean;
  materials: ClassAMaterialsItem | null;
  clocks: ClassAClock[];
  /** The actual listing date (a real event); null until listed. */
  listingDate: string | null;
  /** The planned listing date: the profile option, else the profile's target date. Clocks and Today use it until listed. */
  targetDate: string | null;
  gov: GovrampOverview | null;
  world: ClassAWorld | null;
  rules: ClassARule[];
  /** Rules optional for Class A (folded; not needed to file). */
  optional: ClassARule[];
  counts: ClassAHeroCounts | null;
  refetch: () => void;
}

export function useClassA(slug: string, fr: ProgramCard | null, govCard: ProgramCard | null, opts: { optional?: boolean } = {}): ClassAState {
  const isA = fr?.certification_class === 'A';
  const items = useQuery({ queryKey: queryKeys.classA(slug, fr?.profile_id ?? ''), queryFn: () => getClassA(slug, fr!.profile_id), enabled: isA, retry: false, staleTime: 30_000 });
  const required = useQuery({ queryKey: ['class-a', slug, 'readiness', 'A'], queryFn: () => getClassReadiness(slug, 'A'), enabled: isA, retry: false, staleTime: 60_000 });
  const withOptional = useQuery({ queryKey: ['class-a', slug, 'readiness', 'A', 'optional'], queryFn: () => getClassReadiness(slug, 'A', true), enabled: isA && !!opts.optional, retry: false, staleTime: 60_000 });
  const gov = useQuery({ queryKey: queryKeys.govrampOverview(slug, govCard?.profile_id ?? ''), queryFn: () => getGovrampOverview(slug, govCard!.profile_id), enabled: !!govCard, retry: false, staleTime: 30_000 });
  return useMemo(() => {
    const materials = items.data?.items[0] ?? null;
    const clocks = (items.data?.items.slice(1) ?? []) as ClassAClock[];
    const listingDate = clocks.find((c) => c.anchor === 'class_a_listing_date' && c.anchor_date)?.anchor_date ?? null;
    const targetDate = clocks.find((c) => c.anchor === 'class_a_target_listing_date' && c.anchor_date)?.anchor_date ?? fr?.target_date ?? null;
    const todayDay = (gov.data?.generated_at ?? new Date().toISOString()).slice(0, 10);
    const world = isA && items.data ? classAWorld(materials, gov.data ?? null, listingDate ?? targetDate, todayDay) : null;
    const rules = required.data ? classARules(required.data.requirements, materials, clocks, world) : [];
    const requiredIds = new Set(required.data?.requirements.map((r) => r.requirement_id) ?? []);
    const optional = withOptional.data ? classARules(withOptional.data.requirements.filter((r) => !requiredIds.has(r.requirement_id)), materials, clocks, world) : [];
    return {
      loading: isA && (items.isLoading || required.isLoading || (!!govCard && gov.isLoading)),
      error: !!items.error || !!required.error,
      materials, clocks, listingDate, targetDate, gov: gov.data ?? null, world, rules, optional,
      counts: required.data ? classAHeroCounts(rules) : null,
      refetch: () => { void items.refetch(); void required.refetch(); void gov.refetch(); },
    };
  }, [isA, fr?.target_date, items.data, items.isLoading, items.error, required.data, required.isLoading, required.error, withOptional.data, gov.data, gov.isLoading, govCard]);
}
